import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  ArrowLeft, Loader2, GraduationCap, BookOpen, ExternalLink, Bookmark, BookmarkCheck, Clock, BarChart2,
} from "lucide-react";
import { toast } from "sonner";
import PDFCTABanner from "../components/PDFCTABanner";
import JobsCTABanner from "../components/JobsCTABanner";

const STATUS_OPTIONS = [
  { id: "not_started", label: "Not started" },
  { id: "in_progress", label: "In progress" },
  { id: "completed", label: "Completed" },
];

export default function CareerDetail() {
  const { careerId } = useParams();
  const [career, setCareer] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [educationLevel, setEducationLevel] = useState("");
  const [progress, setProgress] = useState({}); // course_id -> status

  useEffect(() => {
    (async () => {
      try {
        const [careerRes, savedRes, progRes] = await Promise.all([
          api.get(`/quiz/by-career/${careerId}`),
          api.get("/me/careers"),
          api.get("/me/courses"),
        ]);
        setCareer(careerRes.data.career);
        setSaved(savedRes.data.items.some((i) => i.career.career_id === careerId));
        // Fetch latest quiz to know education level (for conditional Job Match CTA)
        try {
          const carRes = savedRes.data.items.find((i) => i.career.career_id === careerId);
          if (carRes?.quiz_id) {
            const q = await api.get(`/quiz/${carRes.quiz_id}`);
            setEducationLevel(q.data?.answers?.education_level || "");
          }
        } catch { /* non-fatal */ }
        const p = {};
        progRes.data.items.forEach((it) => { p[it.course_id] = it.status; });
        setProgress(p);

        setCoursesLoading(true);
        const cRes = await api.post(`/careers/${careerId}/courses`, {
          career_title: careerRes.data.career.title,
        });
        setCourses(cRes.data.courses);
      } catch (e) {
        toast.error("Could not load career details");
      } finally {
        setLoading(false);
        setCoursesLoading(false);
      }
    })();
  }, [careerId]);

  const toggleSave = async () => {
    if (!career) return;
    try {
      if (saved) {
        await api.delete(`/me/careers/${careerId}`);
        setSaved(false);
        toast.success("Removed from saved");
      } else {
        await api.post("/me/careers", { career });
        setSaved(true);
        toast.success("Saved");
      }
    } catch { toast.error("Could not update"); }
  };

  const updateStatus = async (course, status) => {
    setProgress({ ...progress, [course.course_id]: status });
    try {
      await api.put(`/me/courses/${course.course_id}`, {
        status,
        course,
        career_id: careerId,
        career_title: career?.title,
      });
    } catch { toast.error("Could not update status"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="detail-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" />
      </div>
    );
  }

  if (!career) return null;

  const formal = courses.filter((c) => c.type === "formal");
  const informal = courses.filter((c) => c.type === "informal");

  return (
    <main className="min-h-screen bg-[#F4F4F0]" data-testid="career-detail-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <Link to="/dashboard" data-testid="back-to-dashboard" className="inline-flex items-center gap-2 text-sm font-semibold text-[#5C5C55] hover:text-[#002FA7] mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        <div className="grid lg:grid-cols-3 gap-8 mb-14">
          <div className="lg:col-span-2">
            <div className="overline mb-3">[ Career path ]</div>
            <h1 className="font-heading text-5xl sm:text-6xl font-black tracking-tighter leading-[0.95]" data-testid="career-title">
              {career.title}
            </h1>
            <p className="mt-5 text-lg text-[#5C5C55] leading-relaxed max-w-2xl">{career.description}</p>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                onClick={toggleSave}
                data-testid="save-career-detail"
                className={saved ? "btn-primary" : "btn-secondary"}
              >
                {saved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                {saved ? "Saved" : "Save career"}
              </button>
            </div>
          </div>

          <aside className="border border-[#E5E5E0] bg-white p-6 rounded-sm">
            <div className="overline mb-4">Stats</div>
            <div className="space-y-4">
              <Stat label="Match" value={`${career.match_percentage}%`} mono />
              <Stat label="Salary range" value={career.salary_range} />
              <Stat label="Growth outlook" value={career.growth_outlook} />
            </div>
            <div className="mt-6 pt-6 border-t border-[#E5E5E0]">
              <div className="overline mb-3">Key skills</div>
              <div className="flex flex-wrap gap-1.5">
                {career.key_skills.map((s) => (
                  <span key={s} className="font-mono text-xs px-2 py-1 bg-[#F4F4F0] border border-[#E5E5E0] rounded-sm">{s}</span>
                ))}
              </div>
            </div>
          </aside>
        </div>

        {/* PDF CTA — visible right where the user is deciding on this path */}
        <div className="mb-6">
          <PDFCTABanner variant="compact" />
        </div>
        {/* Jobs CTA — for graduates/working professionals only */}
        {career && (
          <div className="mb-14">
            <JobsCTABanner career={career} educationLevel={educationLevel} />
          </div>
        )}

        {/* Courses */}
        <div className="border-t border-[#E5E5E0] pt-12">
          <div className="overline mb-3">[ Learning plan ]</div>
          <h2 className="font-heading text-4xl font-black tracking-tight">Courses to get you there</h2>
          <p className="mt-3 text-[#5C5C55] max-w-2xl">
            A blend of formal credentials and informal resources. Track your progress as you go.
          </p>

          {coursesLoading && (
            <div className="mt-10 flex items-center gap-3 text-[#5C5C55]" data-testid="courses-loading">
              <Loader2 className="w-5 h-5 animate-spin text-[#002FA7]" />
              Curating courses with AI…
            </div>
          )}

          {!coursesLoading && (
            <div className="grid md:grid-cols-2 gap-10 mt-10">
              <CourseColumn
                title="Formal"
                subtitle="Degrees, certifications, accredited programs"
                Icon={GraduationCap}
                courses={formal}
                progress={progress}
                onStatus={updateStatus}
                prefix="formal"
              />
              <CourseColumn
                title="Informal"
                subtitle="Free MOOCs, YouTube, bootcamps, articles"
                Icon={BookOpen}
                courses={informal}
                progress={progress}
                onStatus={updateStatus}
                prefix="informal"
              />
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <div className={`text-lg font-semibold ${mono ? "font-mono text-[#002FA7]" : ""}`}>{value}</div>
    </div>
  );
}

function CourseColumn({ title, subtitle, Icon, courses, progress, onStatus, prefix }) {
  return (
    <div data-testid={`column-${prefix}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 bg-[#0A0A0A] flex items-center justify-center">
          <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
        </div>
        <div>
          <h3 className="font-heading text-2xl font-bold leading-none">{title}</h3>
          <p className="text-sm text-[#5C5C55] mt-1">{subtitle}</p>
        </div>
      </div>
      <div className="mt-6 space-y-4">
        {courses.map((c, idx) => (
          <CourseCard key={c.course_id} c={c} idx={idx} prefix={prefix} status={progress[c.course_id] || "not_started"} onStatus={onStatus} />
        ))}
        {courses.length === 0 && (
          <p className="text-sm text-[#5C5C55] italic">No courses suggested.</p>
        )}
      </div>
    </div>
  );
}

function CourseCard({ c, idx, prefix, status, onStatus }) {
  const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(c.url_hint + " " + c.provider)}`;
  return (
    <div data-testid={`${prefix}-course-${idx}`} className="bg-white border border-[#E5E5E0] rounded-sm p-5 hover:border-[#002FA7]/40 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="overline mb-1">{c.provider}</div>
          <h4 className="font-heading text-lg font-bold leading-tight">{c.title}</h4>
          <p className="text-sm text-[#5C5C55] mt-2 leading-relaxed">{c.description}</p>
        </div>
        <a
          href={searchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 hover:bg-[#F4F4F0] rounded-sm transition-colors"
          title="Search for this course"
          data-testid={`${prefix}-course-link-${idx}`}
        >
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
        <span className="inline-flex items-center gap-1 font-mono px-2 py-1 bg-[#F4F4F0] border border-[#E5E5E0] rounded-sm">
          <Clock className="w-3 h-3" /> {c.duration}
        </span>
        <span className="inline-flex items-center gap-1 font-mono px-2 py-1 bg-[#F4F4F0] border border-[#E5E5E0] rounded-sm">
          <BarChart2 className="w-3 h-3" /> {c.level}
        </span>
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.id}
            data-testid={`${prefix}-status-${idx}-${s.id}`}
            onClick={() => onStatus(c, s.id)}
            className={`text-xs font-mono uppercase tracking-wider px-2.5 py-1.5 rounded-sm border transition-all ${
              status === s.id
                ? s.id === "completed"
                  ? "bg-[#166534] text-white border-[#166534]"
                  : s.id === "in_progress"
                  ? "bg-[#FACC15] text-[#0A0A0A] border-[#FACC15]"
                  : "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                : "bg-white text-[#5C5C55] border-[#E5E5E0] hover:border-[#0A0A0A]"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}
